import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Checkbox from "@mui/material/Checkbox";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import ClearIcon from "@mui/icons-material/Clear";
import SendIcon from "@mui/icons-material/Send";
import EditNoteIcon from "@mui/icons-material/EditNote";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import PushPinIcon from "@mui/icons-material/PushPin";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import ConflictDialog from "../components/ConflictDialog";
import { useTheme } from "@emotion/react";
import Loader from "../../../components/Loader/Loader";
import Fuse from "fuse.js";
import {
  downloadExcelData,
  getBrainReviewData,
  getReviewColumns,
  getBrainCompetitorData,
  handleSubmitReviewData,
  validateUserInput,
} from "../../../services/api";
import APP_CONSTANTS from "../../../utils/config";
import BarChartIcon from "@mui/icons-material/BarChart";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { updateAlertInfo } from "../../../redux/slice/userSlice";
import { useDispatch } from "react-redux";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import TagInput from "../components/TagInput";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import { brainStyles } from "../../../theme/style";
import ColumnVisibilityControl from "../components/ColumnVisibilityControl";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import {
  setLocalStorageData,
  getLocalStorageData,
  isRestrictedKey,
  isLowConfidence,
  parseTagValue,
  isValidValue,
  valueToString,
} from "../components/reviewColumnUtils";
import { useSelector } from "react-redux";
import {
  updatePageSize,
  updateReviewColumnData,
} from "../../../redux/slice/brainSlice";
import { FormControlLabel, Skeleton, Switch, Tab, Tabs } from "@mui/material";
import { InboxOutlined } from "@mui/icons-material";
import { TableVirtuoso } from "react-virtuoso";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SKUDetailsDrawer from "../components/SKUDetailsDrawer";
import { DownloadIcon } from "lucide-react";

const EMPTY_ARRAY = [];

// ----------------------------------------------------------------------
// Memoized Row CELLS
// ----------------------------------------------------------------------

const RowCellsComponent = ({
  row,
  columns,
  isSelected,
  onToggleSelect,
  onCellChange,
  onTagCellChange,
  editedCols,
  has_editable_columns,
  theme,
  classes,
  frozenColumns,
  rowBg,
  frozenLeftOffsets,
  skuCount = 0,
  skuList,
  handleSkuClick = () => {},
  fuseByColumn,
}) => {
  const debounceTimers = useRef({});
  const latestInputValues = useRef({});
  const [invalidInputs, setInvalidInputs] = useState({});
  const formatCellValue = (cell) => {
    if (!cell) return "-";
    return cell.value || "-";
  };
  const renderCell = (colKey, curr_column) => {
    const cell = row[colKey];
    const cellType = curr_column?.type;
    const key = `${row?.id}_${colKey}`;
    const validation = invalidInputs?.[key];
    const is_validating = key in debounceTimers.current ? true : false;
    const fuse = fuseByColumn[colKey];

    const isFrozen = frozenColumns.has(colKey);
    const stickySx = isFrozen
      ? {
          position: "sticky",
          left: frozenLeftOffsets.get(colKey),
          zIndex: 1,
          backgroundColor: rowBg,
          boxShadow: "2px 0 4px -2px rgba(0,0,0,0.08)",
        }
      : {};

    const fmt = (v) => {
      if (cellType === "tag") {
        const tags = parseTagValue(v);
        return (
          <TagInput
            value={tags}
            disabled={true}
            onChange={() => {}}
            maxVisible={2}
            tagLabel={"All Tags"}
          />
        );
      }
      return valueToString(v);
    };

    if (!curr_column.is_editable_column) {
      return (
        <TableCell
          key={colKey}
          className={classes.baseCell}
          sx={{
            fontSize: "0.7rem",
            color: theme.palette.brain.table.row.text,
            ...stickySx,
          }}
        >
          {fmt(cell)}
        </TableCell>
      );
    }

    if (!cell) {
      return (
        <TableCell
          key={colKey}
          className={classes.baseCell}
          sx={{ ...stickySx }}
        >
          <Typography variant="body2" sx={{ color: "text.disabled" }}>
            -
          </Typography>
        </TableCell>
      );
    }

    const confidence_threshold = curr_column.confidence_threshold;
    const canEdit = curr_column.user_edit_access;
    const lowConf = isLowConfidence(cell?.prob || 0, confidence_threshold);
    const edited = editedCols.includes(colKey);

    const cellStyle = {
      backgroundColor: edited
        ? theme.palette.brain.table.row.editedCell
        : theme.palette.brain.grey.background,
      borderLeft: edited
        ? `3px solid ${theme.palette.brain.table.row.editedBorder}`
        : `3px solid ${theme.palette.brain.grey.border}`,
      transition: "all 0.15s ease",
    };

    const nonEditablecellStyle = {
      backgroundColor: theme.palette.brain.grey.background,
      transition: "all 0.15s ease",
    };

    if (canEdit && cellType === "text") {
      return (
        <TableCell key={colKey} className={classes.baseCell} sx={cellStyle}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <input
              value={cell?.value || ""}
              onChange={(e) => {
                const val = e.target.value;
                const key = `${row?.id}_${colKey}`;

                // Your existing behavior — fires immediately
                if (isValidValue(val, colKey)) {
                  onCellChange(row, colKey, val);
                }

                if (curr_column?.can_validate) {
                  latestInputValues.current[key] = val;

                  if (debounceTimers.current[key]) {
                    clearTimeout(debounceTimers.current[key]);
                  }

                  // Start debounce
                  debounceTimers.current[key] = setTimeout(() => {
                    const latestValue = latestInputValues.current[key];

                    const isValid = curr_column.validList.includes(
                      latestValue.toLowerCase()
                    );
                    const results = fuse.search(latestValue);
                    setInvalidInputs((prev) => ({
                      ...prev,
                      [key]: {
                        isValid: isValid,
                        suggestedValue:
                          results.length !== 0 ? results[0].item : null,
                        value: latestValue,
                      },
                    }));

                    delete debounceTimers.current[key];
                  }, 500);
                }
              }}
              placeholder="Enter value..."
              style={{
                flex: 1,
                minWidth: 100,
                fontSize: "14px",
                padding: "5px 8px",
                border: edited
                  ? `1px solid ${theme.palette.brain.table.row.editedBorder}`
                  : `1px solid ${theme.palette.brain.mis.grey.background1}`,
                padding: "8px 8px",
                borderRadius: "6px",
                color: theme.palette.brain.table.row.text,
                outline: "none",
                backgroundColor: theme.palette.brain.table.tableBackground,
              }}
            />
            {edited &&
              (is_validating ? (
                <CircularProgress
                  size={"14px"}
                  sx={{ color: theme.palette.secondary.main2 }}
                />
              ) : validation ? (
                validation?.isValid ? (
                  <CheckCircleIcon
                    sx={{
                      fontSize: 18,
                      color: theme.palette.brain.green.text3,
                    }}
                  />
                ) : (
                  <Tooltip
                    title={
                      validation?.suggestedValue
                        ? `${validation?.value} has not been seen before. Did you mean ${validation?.suggestedValue}?`
                        : `${validation?.value} has not been seen before. Please double-check.`
                    }
                  >
                    <ErrorOutlineIcon
                      sx={{
                        fontSize: 15,
                        color: theme.palette.brain.yellow.text,
                      }}
                    />
                  </Tooltip>
                )
              ) : null)}
            {lowConf && (
              <Tooltip
                title={`Low confidence (${cell.prob} < ${confidence_threshold})`}
                arrow
              >
                <WarningAmberIcon
                  sx={{
                    fontSize: 14,
                    color: theme.palette.brain.table.row.editedBorder,
                  }}
                />
              </Tooltip>
            )}
            <Chip
              label={cell.prob}
              size="small"
              sx={{
                height: 16,
                fontSize: "0.6rem",
                fontWeight: 700,
                ml: 0.25,
                bgcolor:
                  parseFloat(cell.prob) > parseFloat(confidence_threshold)
                    ? theme.palette.brain.green.background
                    : theme.palette.brain.red.background,
                color:
                  parseFloat(cell.prob) > parseFloat(confidence_threshold)
                    ? theme.palette.brain.green.text
                    : theme.palette.brain.red.text,
              }}
            />
          </Box>
        </TableCell>
      );
    }

    if (cellType === "tag") {
      const value = cell.value;
      const tagValues = parseTagValue(value);
      return (
        <TableCell key={colKey} className={classes.baseCell} sx={cellStyle}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Box sx={{ flex: 1, minWidth: 160 }}>
              <TagInput
                value={tagValues}
                disabled={!canEdit}
                onChange={(next) => onTagCellChange(row, colKey, next)}
                maxVisible={2}
              />
            </Box>
            {lowConf && canEdit && (
              <Tooltip
                title={`Low confidence (${cell.prob} < ${confidence_threshold})`}
                arrow
              >
                <WarningAmberIcon
                  sx={{
                    fontSize: 14,
                    color: theme.palette.brain.table.row.editedBorder,
                  }}
                />
              </Tooltip>
            )}
            {canEdit && (
              <Chip
                label={cell.prob}
                size="small"
                sx={{
                  height: 16,
                  fontSize: "0.6rem",
                  fontWeight: 700,
                  ml: 0.25,
                  bgcolor:
                    parseFloat(cell.prob) > parseFloat(confidence_threshold)
                      ? theme.palette.brain.green.background
                      : theme.palette.brain.red.background,
                  color:
                    parseFloat(cell.prob) > parseFloat(confidence_threshold)
                      ? theme.palette.brain.green.text
                      : theme.palette.brain.red.text,
                }}
              />
            )}
          </Box>
        </TableCell>
      );
    }

    return (
      <TableCell
        key={colKey}
        className={classes.baseCell}
        sx={{ ...nonEditablecellStyle, ...stickySx }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography
            variant="body2"
            sx={{
              fontSize: "0.78rem",
              color: theme.palette.brain.table.row.text,
            }}
          >
            {formatCellValue(cell)}
          </Typography>
        </Box>
      </TableCell>
    );
  };

  return (
    <>
      {has_editable_columns && (
        <TableCell
          padding="checkbox"
          className={classes.baseCell}
          sx={{
            position: "sticky",
            left: 0,
            zIndex: 1,
          }}
        >
          <Checkbox
            size="small"
            checked={isSelected}
            onChange={() => onToggleSelect(row.id)}
            sx={{
              color: theme.palette.secondary.main2,
              "&.Mui-checked": {
                color: theme.palette.secondary.main2,
              },
            }}
          />
        </TableCell>
      )}
      <TableCell
        className={classes.baseCell}
        sx={{
          position: "sticky",
          left: has_editable_columns ? 48 : 0,
          borderRight: `1px solid ${theme.palette.brain.grey.border}`,
          zIndex: 1,
        }}
      >
        {skuList?.length === 0 ? (
          "-"
        ) : (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <TagInput
              value={skuList}
              disabled={true}
              onChange={() => {}}
              maxVisible={1}
              tagLabel={"All SKUs"}
            />

            <Box>
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  handleSkuClick(row.upc12, row.description, row.id);
                }}
                size="small"
              >
                <VisibilityOutlinedIcon
                  sx={{
                    fontSize: "16px",
                    color: theme.palette.secondary.main2,
                  }}
                />
              </IconButton>
            </Box>
          </Box>
        )}
      </TableCell>
      {columns.map((col) => renderCell(col.key, col))}
    </>
  );
};

const RowCellsComponentView = ({
  row,
  columns,
  theme,
  classes,
  frozenColumns,
  rowBg,
  frozenLeftOffsets,
  skuCount = 0,
  handleSkuClick = () => {},
}) => {
  const renderCellValue = (colKey, curr_column) => {
    const cell_value = row[colKey];
    const cellType = curr_column?.type;
    const isFrozen = frozenColumns.has(colKey);
    const stickySx = isFrozen
      ? {
          position: "sticky",
          left: frozenLeftOffsets.get(colKey),
          zIndex: 1,
          backgroundColor: rowBg,
          boxShadow: "2px 0 4px -2px rgba(0,0,0,0.08)",
        }
      : {};
    switch (cellType) {
      case "text":
        return (
          <TableCell
            sx={{
              color: theme.palette.brain.table.row.text,
              fontSize: "0.78rem",
              ...stickySx,
            }}
          >
            {cell_value || "–"}
          </TableCell>
        );

      case "tag":
        const tagValues = parseTagValue(cell_value);
        return (
          <TableCell
            sx={{
              color: theme.palette.brain.table.row.text,
              fontSize: "0.78rem",
            }}
          >
            <TagInput
              value={tagValues}
              disabled={true}
              onChange={() => {}}
              maxVisible={2}
            />
          </TableCell>
        );
    }
  };

  return <>{columns.map((col) => renderCellValue(col.key, col))}</>;
};

const areRowPropsEqual = (prevProps, nextProps) => {
  const prevEdits = prevProps.editedCols.join(",");
  const nextEdits = nextProps.editedCols.join(",");

  return (
    prevProps.isSelected === nextProps.isSelected &&
    prevProps.row === nextProps.row &&
    prevEdits === nextEdits &&
    prevProps.columns === nextProps.columns
  );
};

const areRowPropsEqualView = (prevProps, nextProps) => {
  return (
    prevProps.row === nextProps.row && prevProps.columns === nextProps.columns
  );
};

const MemoizedRowCells = React.memo(RowCellsComponent, areRowPropsEqual);
const MemoizedRowCellsView = React.memo(
  RowCellsComponentView,
  areRowPropsEqualView
);

const REVIEW_FILTER_FIELDS = [
  { key: "brand", label: "Brand" },
  { key: "subBrand", label: "Sub Brand" },
  { key: "variant", label: "Variant" },
  { key: "subSegment", label: "Sub Segment" },
];

const EMPTY_REVIEW_FILTER = {
  brand: null,
  subBrand: null,
  variant: null,
  subSegment: null,
};
const EMPTY_UI_FILTERS = {
  brand: "",
  subBrand: "",
  variant: "",
  subSegment: "",
};

const toApiFilter = (filterState) => {
  if (!filterState) return { ...EMPTY_REVIEW_FILTER };
  const source = filterState.filter ?? filterState;
  return {
    brand: source.brand || null,
    subBrand: source.subBrand || null,
    variant: source.variant || null,
    subSegment: source.subSegment || null,
  };
};

const getFilterOptionsFromResponse = (data) =>
  data?.filter_options ?? data?.filter?.filter_options ?? null;

const toApiSearch = (value) => {
  if (typeof value !== "string") return value ?? null;
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
};

const ConfirmDialog = ({
  confirmOpen,
  setConfirmOpen,
  totalSubmitRows,
  editCount,
  theme,
  handleSubmit = () => {},
}) => {
  return (
    <Dialog
      open={confirmOpen}
      onClose={() => setConfirmOpen(false)}
      maxWidth={"xs"}
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          backgroundColor: theme.palette.brain.table.tableBackground,
        },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 700,
          fontSize: "1rem",
          color: theme.palette.secondary.main2,
        }}
      >
        Confirm Submission
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: theme.palette.primary.text2 }}>
          You are about to submit <strong>{totalSubmitRows}</strong> row
          {totalSubmitRows === 1 ? "" : "s"} for approval ({editCount} edited
          cell
          {editCount === 1 ? "" : "s"}).
        </Typography>

        <Typography
          variant="caption"
          sx={{ display: "block", mt: 1, color: theme.palette.primary.text2 }}
        >
          Every editable cell within your access on each row will be submitted
          as <strong>pending</strong>, even if it was not changed.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button
          variant="outlined"
          onClick={() => setConfirmOpen(false)}
          size="small"
          sx={{ textTransform: "none", color: theme.palette.primary.text2 }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          size="small"
          sx={{
            textTransform: "none",
            fontWeight: 700,
            background: theme.palette.secondary.main2,
            color: "#fff",
            "&:hover": { background: theme.palette.secondary.main2 },
          }}
        >
          Confirm Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ----------------------------------------------------------------------
// MAIN PAGE COMPONENT
// ----------------------------------------------------------------------

const DisplayPageSize = ({
  selectedPageSize = 20,
  theme,
  handlePageSizeChange,
}) => {
  const pageSizeList = useMemo(() => {
    return [20, 30, 40];
  }, []);
  return (
    <Select
      labelId={`page-size`}
      value={selectedPageSize}
      onChange={(e) => handlePageSizeChange(e.target.value)}
      renderValue={(selected) => {
        return (
          <>
            <span
              style={{
                color: theme.palette.brain.table.row.text,
              }}
            >
              {selected}
            </span>
          </>
        );
      }}
      sx={{
        height: "35px",
        fontSize: "0.75rem",
        backgroundColor: theme.palette.brain.table.tableBackground,
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: theme.palette.brain.mis.grey.border1,
        },
        "&:hover .MuiOutlinedInput-notchedOutline": {
          borderColor: theme.palette.secondary.main2,
        },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
          border: `1px solid ${theme.palette.secondary.main2}`,
        },
      }}
    >
      {(pageSizeList || []).map((option) => (
        <MenuItem key={option} value={option} sx={{ fontSize: "0.75rem" }}>
          {option}
        </MenuItem>
      ))}
    </Select>
  );
};

const CompetitorsData = ({
  theme,
  loadingRows,
  loadingColumns,
  rows,
  virtuosoComponents,
  classes,
  displayedColumns,
  frozenColumns,
  columnData,
  toggleFreeze,
  skuRecords,
  openSkuForUpc,
}) => {
  const frozenLeftOffsets = useMemo(() => {
    const map = new Map();
    let left = 0;
    displayedColumns.forEach((c) => {
      if (frozenColumns.has(c.key)) {
        map.set(c.key, left);
        left += APP_CONSTANTS.SMALL_WIDTH_COLUMNS.includes(c.key)
          ? 50
          : c.key === "description"
          ? 600
          : c.user_edit_access
          ? 300
          : 200;
      }
    });
    return map;
  }, [displayedColumns, frozenColumns]);
  return (
    <Paper
      elevation={0}
      sx={{
        flex: 1,
        minHeight: 0,
        border: `1px solid ${theme.palette.brain.mis.grey.border1}`,
        backgroundColor: theme.palette.brain.table.tableBackground,
        borderRadius: 2,
        overflow: "hidden",
      }}
    >
      {loadingRows || loadingColumns ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            position: "relative",
            width: "100%",
            height: "100%",
          }}
        >
          <Loader
            message={"Loading data..."}
            showLoadingImage={false}
            top={"50%"}
            circleStyle={{ color: theme.palette.secondary.main2 }}
          />
        </Box>
      ) : rows?.length === 0 ? (
        <>
          <Box
            sx={{
              borderRadius: 1.5,
              py: 6,
              px: 3,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              color: "text.secondary",
              bgcolor: theme.palette.brain.table.background1,
              height: "100%",
              minHeight: "300px",
            }}
          >
            <InboxOutlined
              sx={{
                fontSize: 40,
                color: theme.palette.secondary.main2,
                mb: 1,
              }}
            />
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: theme.palette.brain.table.header.text,
              }}
            >
              No Records found
            </Typography>
          </Box>
        </>
      ) : (
        <TableVirtuoso
          style={{ height: "100%" }}
          data={rows}
          components={virtuosoComponents}
          fixedHeaderContent={() => (
            <TableRow>
              {displayedColumns.map((col) => {
                const isFrozen = frozenColumns.has(col.key);
                const stickyHead = isFrozen
                  ? {
                      position: "sticky",
                      left: frozenLeftOffsets.get(col.key),
                      zIndex: 14,
                      boxShadow: "2px 0 4px -2px rgba(0,0,0,0.08)",
                    }
                  : {};
                return (
                  <TableCell
                    key={col.key}
                    className={classes.tableHeader}
                    sx={{
                      zIndex: 10,
                      minWidth: APP_CONSTANTS.SMALL_WIDTH_COLUMNS.includes(
                        col.key
                      )
                        ? 50
                        : col.key === "description"
                        ? 550
                        : col.user_edit_access
                        ? 300
                        : 200,
                      ...stickyHead,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        whiteSpace: "nowrap",
                        color: "inherit",
                      }}
                    >
                      <span>{col.label}</span>
                      <Tooltip title={col.info} arrow placement="top">
                        <InfoOutlinedIcon
                          sx={{
                            fontSize: 13,
                            color: theme.palette.brain.grey.text,
                            cursor: "pointer",
                          }}
                        />
                      </Tooltip>
                      {APP_CONSTANTS.FREEZABLE_COLUMNS.includes(col.key) && (
                        <Box
                          sx={{
                            flex: 1,
                            display: "flex",
                            justifyContent: "end",
                          }}
                        >
                          <Tooltip
                            title={
                              isFrozen ? "Unfreeze column" : "Freeze column"
                            }
                            arrow
                          >
                            <IconButton
                              size="small"
                              onClick={() => toggleFreeze(col.key)}
                              sx={{ p: 0.25 }}
                            >
                              {isFrozen ? (
                                <PushPinIcon
                                  sx={{
                                    fontSize: 14,
                                    color:
                                      theme.palette.brain.table.header
                                        .restrictedText,
                                  }}
                                />
                              ) : (
                                <PushPinOutlinedIcon
                                  sx={{
                                    fontSize: 14,
                                    color: theme.palette.brain.grey.text,
                                  }}
                                />
                              )}
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </Box>
                  </TableCell>
                );
              })}
            </TableRow>
          )}
          itemContent={(idx, row) => (
            <MemoizedRowCellsView
              row={row}
              idx={idx}
              columns={displayedColumns}
              theme={theme}
              classes={classes}
              frozenColumns={frozenColumns}
              rowBg={() => {
                return idx % 2 === 0
                  ? theme.palette.brain.drawer.item_background
                  : theme.palette.brain.table.row.background;
              }}
              frozenLeftOffsets={frozenLeftOffsets}
              skuCount={skuRecords?.[row.id]?.length || 0}
              handleSkuClick={openSkuForUpc}
            />
          )}
        />
      )}
    </Paper>
  );
};

export default function ReviewerPage() {
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loadingRows, setLoadingRows] = useState(true);
  const [loadingColumns, setLoadingColumns] = useState(true);
  const [edits, setEdits] = useState(new Map());
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [conflicts, setConflicts] = useState([]);
  const [conflictOpen, setConflictOpen] = useState(false);
  const [frozenColumns, setFrozenColumns] = useState(() => {
    const freezed_columns = getLocalStorageData(APP_CONSTANTS.FREEZED_COLUMNS);
    return new Set(freezed_columns);
  });
  const [filterByLowConfidence, setFilterByLowConfidence] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [page, setPage] = useState(1);
  const theme = useTheme();
  const dispatch = useDispatch();
  const [sortkey, setSortKey] = useState({
    sortColumn: "",
    direction: "",
  });

  const classes = brainStyles(theme);
  const columnData = useSelector((state) => state.brain.columnData);
  const [visibleColumns, setVisibleColumns] = useState(new Set([]));
  const [columnOrder, setColumnOrder] = useState([]);
  const [filters, setFilters] = useState({
    brand: "",
    subBrand: "",
    variant: "",
    subSegment: "",
  });
  const [filterOptions, setfilterOptions] = useState({
    brand: [],
    subBrand: [],
    variant: [],
    subSegment: [],
  });
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState(null);
  const [appliedFilter, setAppliedFilter] = useState(EMPTY_REVIEW_FILTER);
  const [fetchTrigger, setFetchTrigger] = useState(0);
  const [tab, setTab] = useState("owns");
  const selectedPageSize = useSelector((state) => state.brain.pageSize);
  const fuseByColumn = useMemo(() => {
    const result = {};

    columnData.columns.forEach((column) => {
      if (column.can_validate) {
        result[column.key] = new Fuse(column.validList, {
          threshold: 0.4,
          includeScore: true,
        });
      }
    });

    return result;
  }, [columnData]);

  const [skuDrawer, setSkuDrawer] = useState({
    open: false,
    data: {},
  });
  const [recordCount, setRecordCount] = useState({
    owns: 0,
    competitors: 0,
  });
  const [skuRecords, setSkuRecords] = useState(null);
  const displayedColumns = useMemo(() => {
    const visibleCols = columnOrder
      .filter((key) => visibleColumns.has(key))
      .map((key) => columnData?.columns.find((col) => col.key === key))
      .filter(Boolean);

    // Separate frozen columns from unfrozen to stick them to the left side
    const frozen = visibleCols.filter((col) => frozenColumns.has(col.key));
    const unfrozen = visibleCols.filter((col) => !frozenColumns.has(col.key));

    return [...frozen, ...unfrozen];
  }, [columnOrder, visibleColumns, columnData, frozenColumns]);

  const [compVisibleColumns, setCompVisibleColumns] = useState(new Set([]));
  const [compColumnOrder, setCompColumnOrder] = useState([]);
  const compDisplayedColumns = useMemo(() => {
    const visibleCols = compColumnOrder
      .filter((key) => compVisibleColumns.has(key))
      .map((key) =>
        columnData?.competitor_columns.find((col) => col.key === key)
      )
      .filter(Boolean);

    const frozen = visibleCols.filter((col) => frozenColumns.has(col.key));
    const unfrozen = visibleCols.filter((col) => !frozenColumns.has(col.key));
    return [...frozen, ...unfrozen];
  }, [compColumnOrder, compVisibleColumns, columnData, frozenColumns]);

  // dynamically determine the active columns based on the selected tab
  const activeColumns =
    tab === "owns" ? displayedColumns : compDisplayedColumns;

  const has_editable_columns = useMemo(() => {
    const columns = columnData?.columns;

    if (columns?.length === 0) {
      return false;
    }
    return columns.some((item) => item?.user_edit_access);
  }, [columnData]);

  const frozenLeftOffsets = useMemo(() => {
    const map = new Map();
    let left = has_editable_columns ? 203 : 155;
    activeColumns.forEach((c) => {
      if (frozenColumns.has(c.key)) {
        map.set(c.key, left);
        left += APP_CONSTANTS.SMALL_WIDTH_COLUMNS.includes(c.key)
          ? 50
          : c.key === "description"
          ? 600
          : c.user_edit_access
          ? 300
          : 200;
      }
    });
    return map;
  }, [activeColumns, frozenColumns, has_editable_columns]);

  const toggleFreeze = (key) => {
    setFrozenColumns((prev) => {
      const n = new Set(prev);
      if (n.has(key)) n.delete(key);
      else n.add(key);

      setLocalStorageData(APP_CONSTANTS.FREEZED_COLUMNS, [...n]);
      return n;
    });
  };
  const handleSortColumn = (columnObj) => {
    setPage(1);
    let column_name = "";
    let direction = "";
    if (columnObj.key !== sortkey?.sortColumn) {
      column_name = columnObj.key;
      direction = "asc";
    } else if (sortkey?.direction === "asc") {
      column_name = columnObj.key;
      direction = "desc";
    }
    setSortKey({
      sortColumn: column_name,
      direction: direction,
    });
  };

  const localStorageSetting = useCallback(
    (all_columns, key_reference_columns, comp_all_columns) => {
      if (!all_columns?.length || !comp_all_columns?.length) return;

      // =========================
      // UPC Columns
      // =========================

      let visibleColumn_data = getLocalStorageData(
        APP_CONSTANTS.VISIBLE_COLUMN
      );
      let columnOrder = getLocalStorageData(APP_CONSTANTS.COLUMN_ORDER);

      const all_column_list = all_columns.map((c) => c.key);
      const user_editable_columns = all_columns
        .filter((c) => c.user_edit_access === true)
        .map((c) => c.key);

      const isSame =
        all_column_list.length === columnOrder.length &&
        all_column_list.every((item) => columnOrder.includes(item));
      const isIncluded = user_editable_columns.every((item) =>
        visibleColumn_data.includes(item)
      );

      if (
        visibleColumn_data?.length === 0 ||
        columnOrder?.length === 0 ||
        !isSame ||
        !isIncluded
      ) {
        const other_non_editable_columns = all_columns
          .filter(
            (item) =>
              item.is_editable_column === false &&
              !key_reference_columns.includes(item.key)
          )
          .map((item) => item.key);
        const user_non_editable_columns = all_columns
          .filter(
            (item) =>
              item.is_editable_column === true &&
              item.user_edit_access === false
          )
          .map((item) => item.key);

        visibleColumn_data = [
          ...key_reference_columns,
          ...user_editable_columns,
        ];
        columnOrder = [
          ...key_reference_columns,
          ...user_editable_columns,
          ...user_non_editable_columns,
          ...other_non_editable_columns,
        ];
        setLocalStorageData(APP_CONSTANTS.VISIBLE_COLUMN, visibleColumn_data);
        setLocalStorageData(APP_CONSTANTS.COLUMN_ORDER, columnOrder);
      }

      if (visibleColumns?.size === 0 || columnOrder?.length === 0) {
        setVisibleColumns(new Set(visibleColumn_data));
        setColumnOrder(columnOrder);
      }

      // =========================
      // Competitor Columns
      // =========================

      let competitor_visibleColumn = getLocalStorageData(
        APP_CONSTANTS.COMPETITOR_VISIBLE_COLUMN
      );

      let competitor_columnOrder = getLocalStorageData(
        APP_CONSTANTS.COMPETITOR_COLUMN_ORDER
      );

      const comp_all_column_list = comp_all_columns.map((c) => c.key);

      const isSame_comp =
        comp_all_column_list.length === competitor_columnOrder.length &&
        comp_all_column_list.every((item) =>
          competitor_columnOrder.includes(item)
        );

      if (
        competitor_visibleColumn?.length === 0 ||
        competitor_columnOrder?.length === 0 ||
        !isSame_comp
      ) {
        const all_non_editable_columns = comp_all_columns
          .filter((item) => !key_reference_columns.includes(item.key))
          .map((item) => item.key);

        competitor_visibleColumn = [
          ...key_reference_columns,
          ...all_non_editable_columns,
        ];

        competitor_columnOrder = [
          ...key_reference_columns,
          ...all_non_editable_columns,
        ];

        setLocalStorageData(
          APP_CONSTANTS.COMPETITOR_VISIBLE_COLUMN,
          competitor_visibleColumn
        );

        setLocalStorageData(
          APP_CONSTANTS.COMPETITOR_COLUMN_ORDER,
          competitor_columnOrder
        );
      }

      if (compVisibleColumns?.size === 0 || compColumnOrder?.length === 0) {
        setCompVisibleColumns(new Set(competitor_visibleColumn));
        setCompColumnOrder(competitor_columnOrder);
      }

      setLoadingColumns(false);
    },
    [
      visibleColumns?.size,
      columnOrder?.length,
      compVisibleColumns?.size,
      compColumnOrder?.length,
    ]
  );

  const loadColumnData = useCallback(async () => {
    try {
      setLoadingColumns(true);
      const resp = await getReviewColumns();
      const statusCode = resp.data.status.statusCode;
      if (statusCode === "nam-200") {
        const columns = resp.data.data?.upc_columns;
        const reference_columns = columns
          .filter((item) => !item.is_editable_column)
          .map((item) => item.key);
        const restricted_columns = columns
          .filter((item) => item.is_editable_column && !item.user_edit_access)
          .map((item) => item.key);
        const editable_columns = columns
          .filter((item) => item.user_edit_access)
          .map((item) => item.key);

        dispatch(
          updateReviewColumnData({
            columns: columns,
            key_reference_columns: APP_CONSTANTS.KEY_REFERENCE_COLUMNS,
            reference_columns: reference_columns,
            tag_columns: APP_CONSTANTS.TAG_COLUMNS,
            restricted_columns: restricted_columns,
            editable_columns: editable_columns,
            sku_columns: resp.data.data?.sku_columns,
            competitor_columns: resp.data.data.competitor_columns,
            competitor_reference_columns: resp.data.data.competitor_columns
              .filter((item) => !item.is_editable_column)
              .map((item) => item.key),
          })
        );
      }
    } catch (e) {
      console.error(e);
    }
  }, [dispatch]);

  const loadRowData = useCallback(
    async (
      p = 1,
      page_size = APP_CONSTANTS.BRAIN_PAGE_SIZE,
      filterState = EMPTY_REVIEW_FILTER,
      searchQuery = null,
      current_tab,
      sortColumn = "",
      direction = "",
      filter_by_low_confidence = filterByLowConfidence
    ) => {
      const apiFilter = toApiFilter(filterState);
      const apiSearch = toApiSearch(searchQuery);
      setLoadingRows(true);
      try {
        const res_api =
          current_tab === "owns"
            ? await getBrainReviewData(
                p,
                page_size,
                apiFilter,
                apiSearch,
                sortColumn,
                direction,
                filter_by_low_confidence
              )
            : await getBrainCompetitorData(p, page_size, apiFilter, apiSearch);
        const res = res_api.data.data;
        // const res = current_tab === "owns" ? reviewData : brainCompetitorData;
        setRows(res.rows);
        setPagination(res.pagination);
        if (current_tab === "owns") {
          setRecordCount(res.record_count);
        }

        setSkuRecords(res?.upc_sku_map);

        const nextFilterOptions = getFilterOptionsFromResponse(res);
        if (nextFilterOptions) {
          setfilterOptions(nextFilterOptions);
        }
      } catch (error) {
        dispatch(
          updateAlertInfo({
            message: "Failed to load review data",
            open: true,
            severity: "error",
          })
        );
      } finally {
        setLoadingRows(false);
      }
    },
    [dispatch]
  );

  useEffect(() => {
    if (columnData?.columns?.length > 0) {
      localStorageSetting(
        columnData.columns,
        columnData.key_reference_columns,
        columnData.competitor_columns
      );
    } else {
      loadColumnData();
    }
  }, [columnData, loadColumnData, localStorageSetting]);

  useEffect(() => {
    if (loadingColumns) return;
    loadRowData(
      page,
      selectedPageSize,
      appliedFilter,
      appliedSearch,
      tab,
      sortkey.sortColumn,
      sortkey.direction,
      filterByLowConfidence
    );
  }, [
    page,
    sortkey.sortColumn,
    sortkey.direction,
    loadingColumns,
    fetchTrigger,
  ]);

  const handleCellChange = useCallback((row, colKey, newValue) => {
    const cell = row[colKey];
    const oldValue = cell?.value ?? "";
    const key = `${row.id}-${colKey}`;

    setEdits((prev) => {
      const next = new Map(prev);
      const existing = prev.get(key);
      const origOld = existing ? existing.oldValue : oldValue;
      if (String(newValue) === String(origOld)) {
        next.delete(key);
      } else {
        next.set(key, {
          rowId: row.id,
          column: colKey,
          oldValue: origOld,
          newValue: newValue,
        });
      }
      return next;
    });

    setRows((prev) =>
      prev.map((r) =>
        r.id === row.id
          ? { ...r, [colKey]: { ...r[colKey], value: newValue } }
          : r
      )
    );

    setSelectedIds((prev) => {
      if (prev.has(row.id)) return prev;
      const next = new Set(prev);
      next.add(row.id);
      return next;
    });
  }, []);

  const toggleSelect = useCallback((id) => {
    setSelectedIds((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    if (selectedIds.size === rows?.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(rows.map((r) => r.id)));
  }, [rows, selectedIds.size]);

  const handleApplyFilters = () => {
    const apiFilter = toApiFilter(filters);
    const apiSearch = toApiSearch(search);
    setAppliedFilter(apiFilter);
    setAppliedSearch(apiSearch);
    if (page !== 1) {
      setPage(1);
    } else {
      setFetchTrigger((t) => t + 1);
    }
  };

  const handleSearch = () => {
    const apiSearch = toApiSearch(search);
    setAppliedSearch(apiSearch);
    if (page !== 1) {
      setPage(1);
    } else {
      setFetchTrigger((t) => t + 1);
    }
  };

  const handleClearSearch = () => {
    setSearch("");
    setAppliedSearch(null);
    if (page !== 1) {
      setPage(1);
    } else {
      setFetchTrigger((t) => t + 1);
    }
  };

  const handleClearFilters = () => {
    setFilters(EMPTY_UI_FILTERS);
    setSearch("");
    setAppliedFilter(EMPTY_REVIEW_FILTER);
    setAppliedSearch(null);
    if (page !== 1) {
      setPage(1);
    } else {
      setFetchTrigger((t) => t + 1);
    }
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  const handleSubmit = async () => {
    setConfirmOpen(false);
    setSubmitting(true);
    const editList = Array.from(edits.values());
    const toSubmit = editList.filter((e) => selectedIds.has(e.rowId));
    const editRowIds = new Set(editList.map((e) => e.rowId));
    const missingSelectedIds = [...selectedIds].filter(
      (id) => !editRowIds.has(id)
    );

    const payload = {
      changes: toSubmit,
      other_rows: missingSelectedIds,
    };

    try {
      const response = await handleSubmitReviewData({ payload: payload });

      if (
        response?.data?.status?.statusCode === "nam-200" ||
        response?.data?.status?.statusCode === 409
      ) {
        const res = response.data.data;
        if (response?.data?.status?.statusCode === "nam-200") {
          dispatch(
            updateAlertInfo({
              message:
                res?.skipped?.length > 0
                  ? "Partially submitted. Please review conflicted entries"
                  : "Review submitted successfully",
              open: true,
              severity: "success",
            })
          );
        } else {
          dispatch(
            updateAlertInfo({
              message:
                "No entries could be submitted. Please review all conflicts",
              open: true,
              severity: "error",
            })
          );
        }

        if (res?.skipped?.length > 0) {
          const conflict_row_ids = res.skipped || [];
          const conflict_rows = rows?.filter((item) =>
            conflict_row_ids.includes(item.id)
          );
          setConflicts(conflict_rows);
          setConflictOpen(true);
        }

        setRows((prev) =>
          prev.filter(
            (item) =>
              !(editRowIds.has(item.id) || missingSelectedIds.includes(item.id))
          )
        );

        loadRowData(
          1,
          selectedPageSize,
          appliedFilter,
          appliedSearch,
          tab,
          sortkey.sortColumn,
          sortkey.direction,
          filterByLowConfidence
        );

        setEdits((prev) => {
          const n = new Map(prev);
          toSubmit.forEach((e) => n.delete(`${e.rowId}-${e.column}`));
          return n;
        });
        setSelectedIds(new Set());
      }
    } catch (error) {
      dispatch(
        updateAlertInfo({
          message: "Failed to submit: Internal Server Error",
          open: true,
          severity: "error",
        })
      );
    }
    setSubmitting(false);
  };

  const handleExportData = async () => {
    const visibleColumnKeys =
      getLocalStorageData(APP_CONSTANTS.VISIBLE_SKU_COLUMN) || [];
    const payload = {
      search: "",
      filter: {
        brand: filters?.brand || "",
        subBrand: filters?.subBrand || "",
        variant: filters?.variant || "",
        subSegment: filters?.subSegment || "",
        row_ids: [...selectedIds],
      },
      filter_by_low_confidence: filterByLowConfidence,
      visible_columns: displayedColumns?.map((item) => {
        return { key: item.key, label: item.label };
      }),
      visible_sku_columns: columnData.sku_columns.filter(
        (item) =>
          visibleColumnKeys.length === 0 || visibleColumnKeys.includes(item.key)
      ),
    };

    try {
      setIsDownloading(true);
      const response = await downloadExcelData({ payload: payload });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "Brain_review_data.xlsx";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {}
    setIsDownloading(false);
  };

  const rowEdits = useMemo(() => {
    const map = {};
    for (const [key, val] of edits.entries()) {
      if (!map[val.rowId]) map[val.rowId] = [];
      map[val.rowId].push(val.column);
    }
    return map;
  }, [edits]);

  const editCount = edits.size;

  const handlePageSizeChange = (page_size) => {
    dispatch(updatePageSize(page_size));
    loadRowData(
      1,
      page_size,
      appliedFilter,
      appliedSearch,
      tab,
      sortkey.sortColumn,
      sortkey.direction,
      filterByLowConfidence
    );
  };

  // =========================================================================
  // FIX: Stably memoized Virtuoso components to prevent unmounting/lag loop
  // =========================================================================
  const virtuosoComponents = useMemo(() => {
    return {
      Scroller: React.forwardRef((props, ref) => (
        <TableContainer
          component={Box}
          {...props}
          ref={ref}
          sx={{
            "&::-webkit-scrollbar": { height: 6, width: 6 },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: theme.palette.brain.mis.grey.background1,
              borderRadius: 3,
            },
          }}
        />
      )),
      Table: (props) => (
        <Table
          {...props}
          stickyHeader
          size="small"
          sx={{ borderCollapse: "separate" }}
        />
      ),
      TableHead: React.forwardRef((props, ref) => (
        <TableHead {...props} ref={ref} />
      )),
      TableRow: (props) => {
        const idx = props["data-index"];
        const rowBg =
          idx % 2 === 0
            ? theme.palette.brain.drawer.item_background
            : theme.palette.brain.table.row.background;
        return (
          <TableRow
            {...props}
            sx={{
              backgroundColor: rowBg,
              "& > td": { backgroundColor: rowBg },
              "&:hover": {
                // backgroundColor: theme.palette.brain.table.row.hoverBackground,
              },
              transition: "background-color 0.12s ease",
            }}
          />
        );
      },
      TableBody: React.forwardRef((props, ref) => (
        <TableBody {...props} ref={ref} />
      )),
      // Built-in Empty state prevents the rendering gap flicker
      EmptyPlaceholder: () => (
        <Box
          sx={{
            border: `1px dashed ${theme.palette.brain.grey.border}`,
            borderRadius: 1.5,
            py: 6,
            px: 3,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            color: "text.secondary",
            bgcolor: theme.palette.brain.table.background1,
            height: "100%",
            minHeight: "300px",
          }}
        >
          <InboxOutlined
            sx={{ fontSize: 40, color: theme.palette.secondary.main2, mb: 1 }}
          />
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              color: theme.palette.brain.table.header.text,
            }}
          >
            No Records found
          </Typography>
        </Box>
      ),
    };
  }, [theme]); // only rebuild if the theme changes

  const openSkuForUpc = (upc12, description, row_id) => {
    const sku_rows = skuRecords?.[row_id] || [];
    const sku_columns = columnData.sku_columns;

    setSkuDrawer({
      open: true,
      data: {
        row_id: row_id,
        upc: upc12,
        description: description,
        sku_details: {
          rows: sku_rows,
          columns: sku_columns,
        },
      },
    });
  };

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 1,
        p: 2.5,
      }}
    >
      {/* Header Bar */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 1,
          // py: 1,
          borderRadius: 2,
          paddingBottom: "0px",
          gap: 2,
        }}
      >
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            overflow: "hidden",
          }}
        >
          <Typography
            variant="h1"
            sx={{
              fontSize: "1.35rem",
              fontWeight: 700,
              display: "flex",
              alignItems: "center",
              gap: 1,
              color: theme.palette.secondary.main2,
            }}
          >
            <EditNoteIcon
              sx={{ color: theme.palette.secondary.main2, fontSize: "30px" }}
            />
            Review Data
          </Typography>

          <Typography
            variant="body2"
            noWrap
            title={"Validate and edit product attributes"}
            sx={{
              color: theme.palette.primary.text2,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              width: "100%",
              display: "block",
            }}
          >
            Validate and edit product attributes
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: 1.5,
            alignItems: "center",
            flexShrink: 0,
          }}
        >
          {editCount > 0 && (
            <Chip
              label={`${editCount} change${editCount > 1 ? "s" : ""}`}
              size="small"
              sx={{
                fontWeight: 700,
                fontSize: "0.7rem",
                backgroundColor: theme.palette.brain.yellow.background,
                color: theme.palette.brain.yellow.text,
                border: `1px solid ${theme.palette.brain.yellow.border}`,
              }}
            />
          )}

          {selectedIds.size > 0 && (
            <Chip
              label={`${selectedIds.size} selected`}
              size="small"
              sx={{
                fontWeight: 600,
                fontSize: "0.7rem",
                backgroundColor: theme.palette.brain.blue.background,
                color: theme.palette.brain.blue.text,
              }}
            />
          )}

          {tab === "owns" && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon size={16} />}
              onClick={handleExportData}
              disabled={isDownloading}
              sx={{
                textTransform: "none",
                fontSize: "0.75rem",
                fontWeight: 600,
                borderColor: theme.palette.brain.table.row.text,
                color: theme.palette.brain.table.row.text,
                "&:hover": {
                  borderColor: theme.palette.secondary.main2,
                  color: theme.palette.secondary.main2,
                },
                "&:disabled": {
                  borderColor: theme.palette.brain.grey.border,
                  color: theme.palette.brain.grey.text,
                  boxShadow: "none",
                },
              }}
            >
              {selectedIds.size > 0
                ? "Export Selected UPC to Excel"
                : "Export to Excel"}
            </Button>
          )}

          {!loadingColumns &&
            (tab === "owns" ? (
              <ColumnVisibilityControl
                columns={columnData?.columns || []}
                allowedColumns={columnData?.editable_columns}
                keyRefColumns={columnData?.key_reference_columns}
                refColumns={columnData?.reference_columns}
                visible={visibleColumns}
                onChange={setVisibleColumns}
                columnOrder={columnOrder}
                onReorder={setColumnOrder}
                theme={theme}
                loading={loadingRows}
                readOnly={false}
                visibleColumnKey={APP_CONSTANTS.VISIBLE_COLUMN}
                columnOrderKey={APP_CONSTANTS.COLUMN_ORDER}
              />
            ) : (
              <ColumnVisibilityControl
                columns={columnData?.competitor_columns || []}
                allowedColumns={[]}
                keyRefColumns={columnData?.key_reference_columns}
                refColumns={columnData?.competitor_reference_columns}
                visible={compVisibleColumns}
                onChange={setCompVisibleColumns}
                columnOrder={compColumnOrder}
                onReorder={setCompColumnOrder}
                theme={theme}
                loading={loadingRows}
                readOnly={true}
                visibleColumnKey={APP_CONSTANTS.COMPETITOR_VISIBLE_COLUMN}
                columnOrderKey={APP_CONSTANTS.COMPETITOR_COLUMN_ORDER}
              />
            ))}

          {tab === "owns" && has_editable_columns && (
            <Button
              variant="contained"
              size="small"
              disabled={selectedIds.size === 0 || submitting}
              onClick={() => setConfirmOpen(true)}
              startIcon={
                submitting ? <CircularProgress size={14} /> : <SendIcon />
              }
              sx={{
                textTransform: "none",
                fontSize: "0.75rem",
                fontWeight: 700,
                background: theme.palette.secondary.main2,
                "&:hover": {
                  background: theme.palette.secondary.main2,
                },
                "&:disabled": {
                  background: "#e2e8f0",
                  boxShadow: "none",
                },
                color: "#fff",
              }}
            >
              Submit for Review
            </Button>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          gap: 2,
          px: 1,
          flexWrap: "wrap",
          borderBottom: "0.5px solid #ccc",
          paddingBottom: "8px",
        }}
      >
        <Tabs
          value={tab}
          onChange={(_, v) => {
            setTab(v);
            loadRowData(
              1,
              selectedPageSize,
              appliedFilter,
              appliedSearch,
              v,
              sortkey.sortColumn,
              sortkey.direction,
              filterByLowConfidence
            );
          }}
          sx={{
            minHeight: 36,
            "& .MuiTab-root": {
              minHeight: 36,
              textTransform: "none",
              fontSize: "0.78rem",
              fontWeight: 600,
              px: 1.75,
              color: theme.palette.brain.table.row.text,
            },
            "& .MuiTabs-indicator": {
              backgroundColor: theme.palette.secondary.main2,
              height: 2.5,
            },
          }}
        >
          <Tab
            label={`MJN Data (${loadingRows ? "..." : recordCount?.owns || 0})`}
            value="owns"
          />
          <Tab
            label={`Competitors Data (${
              loadingRows ? "..." : recordCount?.competitors || 0
            })`}
            value="competitors"
          />
        </Tabs>
        {loadingRows ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              flexShrink: 0,
              ml: "auto",
              justifyContent: "end",
            }}
          >
            <Skeleton
              variant="rounded"
              sx={{ minWidth: "350px", height: "30px" }}
            />
            <Skeleton
              variant="rounded"
              sx={{ minWidth: "64px", height: "29px" }}
            />
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              flexShrink: 0,
              ml: "auto",
              justifyContent: "end",
            }}
          >
            <TextField
              size="small"
              placeholder="Search products..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              InputProps={{
                endAdornment:
                  search.trim() || appliedSearch ? (
                    <InputAdornment position="end">
                      <Tooltip title="Clear search">
                        <IconButton
                          size="small"
                          aria-label="Clear search"
                          onClick={handleClearSearch}
                          onMouseDown={(e) => e.preventDefault()}
                          edge="end"
                          sx={{
                            mr: 0.25,
                            color: theme.palette.secondary.main2,
                            opacity: 0.85,
                            "&:hover": {
                              opacity: 1,
                              backgroundColor: theme.palette.action.hover,
                            },
                          }}
                        >
                          <ClearIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ) : null,
              }}
              sx={{
                width: 280,
                minWidth: 160,
                "& .MuiOutlinedInput-root": {
                  fontSize: "0.75rem",
                  color: theme.palette.brain.table.row.text,
                  pr: search.trim() || appliedSearch ? 0.5 : undefined,
                  backgroundColor: theme.palette.brain.table.tableBackground,
                  "& fieldset": {
                    borderColor: theme.palette.brain.mis.grey.border1,
                  },
                  "&:hover fieldset": {
                    borderColor: theme.palette.secondary.main2,
                  },
                  "&.Mui-focused fieldset": {
                    border: `1px solid ${theme.palette.secondary.main2}`,
                  },
                },
              }}
            />
            <Button
              variant="outlined"
              size="small"
              onClick={handleSearch}
              sx={{
                flexShrink: 0,
                textTransform: "none",
                fontSize: "0.75rem",
                borderColor: theme.palette.secondary.main2,
                color: theme.palette.secondary.main2,
                height: "29px",
                "&:hover": {
                  borderColor: theme.palette.secondary.main2,
                  color: theme.palette.secondary.main2,
                },
              }}
            >
              Search
            </Button>
          </Box>
        )}
      </Box>

      <>
        {/* Status legend */}
        {tab === "owns" && (
          <Box sx={{ display: "flex", alignItems: "end", gap: 2 }}>
            {has_editable_columns && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-end",
                  gap: 2,
                  flexWrap: "wrap",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <WarningAmberIcon
                    sx={{
                      fontSize: 14,
                      color: theme.palette.brain.table.row.editedBorder,
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: "0.65rem",
                      color: theme.palette.primary.text2,
                      textTransform: "capitalize",
                    }}
                  >
                    Low confidence
                  </Typography>

                  {loadingRows ? (
                    <>
                      <Skeleton
                        variant="rounded"
                        sx={{
                          width: "60px",
                          height: "21px",
                          borderRadius: "20px",
                        }}
                      />
                    </>
                  ) : (
                    <Tooltip
                      title="Cells with confidence score below threshold"
                      arrow
                    >
                      <Box
                        sx={{
                          padding: "1px 8px",
                          fontWeight: 700,
                          fontSize: "0.7rem",
                          backgroundColor:
                            theme.palette.brain.yellow.background,
                          color: theme.palette.brain.yellow.text,
                          border: `1px solid ${theme.palette.brain.yellow.border}`,
                          borderRadius: "20px",
                        }}
                      >
                        {/* {pagination?.total_low_confidence_cells || 0} Cells */}
                        {pagination?.total_low_confidence_cells || 0} Cell
                        {pagination?.total_low_confidence_cells > 1 ? "s" : ""}
                      </Box>
                    </Tooltip>
                  )}

                  <FormControlLabel
                    labelPlacement="start"
                    control={
                      <Switch
                        size="small"
                        checked={filterByLowConfidence}
                        disabled={loadingColumns || loadingRows}
                        onChange={(e) => {
                          setFilterByLowConfidence(e.target.checked);
                          if (page !== 1) {
                            setPage(1);
                          } else {
                            loadRowData(
                              1,
                              selectedPageSize,
                              appliedFilter,
                              appliedSearch,
                              tab,
                              sortkey.sortColumn,
                              sortkey.direction,
                              e.target.checked
                            );
                          }
                        }}
                        sx={{
                          "& .MuiSwitch-switchBase.Mui-checked": {
                            color: theme.palette.brain.yellow.text,
                          },
                          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                            {
                              backgroundColor: theme.palette.brain.yellow.text,
                              opacity: 0.5,
                            },

                          // Disabled thumb
                          "& .MuiSwitch-switchBase.Mui-disabled": {
                            color: theme.palette.grey[5],
                          },

                          // Disabled track
                          "& .MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track":
                            {
                              backgroundColor: theme.palette.grey[5],
                              opacity: 0.3,
                            },

                          "& .MuiSwitch-track": {
                            backgroundColor: theme.palette.brain.table.row.text,
                            opacity: 0.4,
                          },
                        }}
                      />
                    }
                    label={
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: "0.72rem",
                          fontWeight: 600,
                          color: theme.palette.primary.text2,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {" "}
                        | Show Low Confidence Rows
                      </Typography>
                    }
                    sx={{ ml: 0, mr: 0.5 }}
                  />
                </Box>
              </Box>
            )}
          </Box>
        )}

        {/* Filters */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
            px: 2,
            py: 1.5,
            borderRadius: 1.5,
            backgroundColor: theme.palette.brain.cyanBlue.background,
            border: `1px solid ${theme.palette.brain.cyanBlue.border}`,
          }}
        >
          {loadingRows ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                flexShrink: 0,
                ml: "auto",
                justifyContent: "end",
                width: "100%",
              }}
            >
              <Skeleton variant="rounded" sx={{ height: "30px", flex: 1 }} />
              <Skeleton
                variant="rounded"
                sx={{ minWidth: "74px", height: "29px" }}
              />
              <Skeleton
                variant="rounded"
                sx={{ minWidth: "64px", height: "29px" }}
              />
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flex: 1,
                  gap: 2,
                  minWidth: 0,
                }}
              >
                {REVIEW_FILTER_FIELDS.map(({ key, label }) => (
                  <FormControl
                    key={key}
                    size="small"
                    sx={{ flex: 1, minWidth: 0, maxWidth: 280 }}
                  >
                    <Select
                      labelId={`review-filter-${key}-label`}
                      displayEmpty
                      value={filters[key] || ""}
                      onChange={(e) =>
                        setFilters((prev) => ({
                          ...prev,
                          [key]: e.target.value,
                        }))
                      }
                      renderValue={(selected) => {
                        if (!selected) {
                          return (
                            <Typography
                              component="span"
                              sx={{
                                fontSize: "0.75rem",
                                color: theme.palette.brain.table.row.text,
                                opacity: 0.65,
                              }}
                            >
                              Select {label}
                            </Typography>
                          );
                        }
                        return (
                          <>
                            <Typography
                              component="span"
                              sx={{
                                color: theme.palette.brain.table.row.text,
                                fontSize: "0.75rem",
                                fontWeight: 700,
                              }}
                            >
                              {label}:
                            </Typography>{" "}
                            <span
                              style={{
                                color: theme.palette.brain.table.row.text,
                              }}
                            >
                              {selected}
                            </span>
                          </>
                        );
                      }}
                      sx={{
                        fontSize: "0.75rem",
                        backgroundColor:
                          theme.palette.brain.table.tableBackground,
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: theme.palette.brain.mis.grey.border1,
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: theme.palette.secondary.main2,
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          border: `1px solid ${theme.palette.secondary.main2}`,
                        },
                      }}
                    >
                      <MenuItem value="" sx={{ fontSize: "0.75rem" }}>
                        <em>Select {label}</em>
                      </MenuItem>
                      {(filterOptions[key] || []).map((option) => (
                        <MenuItem
                          key={option}
                          value={option}
                          sx={{ fontSize: "0.75rem" }}
                        >
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ))}
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  flexShrink: 0,
                }}
              >
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleApplyFilters}
                  sx={{
                    textTransform: "none",
                    fontSize: "0.75rem",
                    borderColor: theme.palette.secondary.main2,
                    color: theme.palette.secondary.main2,
                    "&:hover": {
                      borderColor: theme.palette.secondary.main2,
                      color: theme.palette.secondary.main2,
                    },
                  }}
                >
                  Apply Filters
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleClearFilters}
                  sx={{
                    textTransform: "none",
                    fontSize: "0.75rem",
                    borderColor: theme.palette.brain.mis.grey.border1,
                    color: theme.palette.primary.text2,
                    "&:hover": {
                      borderColor: theme.palette.brain.mis.grey.border1,
                      color: theme.palette.primary.text2,
                    },
                  }}
                >
                  Clear
                </Button>
              </Box>
            </>
          )}
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, px: 1 }}>
          <InfoOutlinedIcon sx={{ fontSize: 13, color: "#b45309" }} />
          <Typography
            variant="caption"
            sx={{ fontSize: "0.68rem", fontWeight: 600, color: "#92400e" }}
          >
            These are AI-generated product attributes. Please review and
            validate them before approval.
          </Typography>
        </Box>
        {/* Table Section */}
        {tab === "owns" ? (
          <Paper
            elevation={0}
            sx={{
              flex: 1,
              minHeight: 0,
              border: `1px solid ${theme.palette.brain.mis.grey.border1}`,
              backgroundColor: theme.palette.brain.table.tableBackground,
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            {loadingRows || loadingColumns ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  position: "relative",
                  width: "100%",
                  height: "100%",
                }}
              >
                <Loader
                  message={"Loading data..."}
                  showLoadingImage={false}
                  top={"50%"}
                  circleStyle={{ color: theme.palette.secondary.main2 }}
                />
              </Box>
            ) : rows?.length === 0 ? (
              <>
                <Box
                  sx={{
                    borderRadius: 1.5,
                    py: 6,
                    px: 3,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "text.secondary",
                    bgcolor: theme.palette.brain.table.background1,
                    height: "100%",
                    minHeight: "300px",
                  }}
                >
                  <InboxOutlined
                    sx={{
                      fontSize: 40,
                      color: theme.palette.secondary.main2,
                      mb: 1,
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.brain.table.header.text,
                    }}
                  >
                    No Records found
                  </Typography>
                </Box>
              </>
            ) : (
              <TableVirtuoso
                style={{ height: "100%" }}
                data={rows}
                // initialItemCount={15} // INSTANT render on first frame, eliminates the gap
                components={virtuosoComponents} // USE THE MEMOIZED COMPONENTS!
                fixedHeaderContent={() => (
                  <TableRow>
                    {has_editable_columns && (
                      <TableCell
                        padding="checkbox"
                        className={classes.tableHeader}
                        sx={{
                          position: "sticky",
                          left: 0,
                          width: 42,
                          zIndex: 12,
                        }}
                      >
                        <Checkbox
                          size="small"
                          checked={
                            selectedIds.size === rows?.length &&
                            rows?.length > 0
                          }
                          indeterminate={
                            selectedIds.size > 0 &&
                            selectedIds.size < rows?.length
                          }
                          onChange={toggleSelectAll}
                          sx={{
                            color: theme.palette.secondary.main2,
                            "&.Mui-checked": {
                              color: theme.palette.secondary.main2,
                            },
                          }}
                        />
                      </TableCell>
                    )}
                    <TableCell
                      className={classes.tableHeader}
                      sx={{
                        position: "sticky",
                        left: has_editable_columns ? 48 : 0,
                        zIndex: 12,
                        borderRight: `1px solid ${theme.palette.brain.grey.border}`,
                        minWidth: "155px",
                      }}
                    >
                      Active SKU
                    </TableCell>
                    {activeColumns.map((col) => {
                      const isRestricted = isRestrictedKey(
                        col.key,
                        columnData?.restricted_columns
                      );
                      const isFrozen = frozenColumns.has(col.key);
                      const stickyHead = isFrozen
                        ? {
                            position: "sticky",
                            left: frozenLeftOffsets.get(col.key),
                            zIndex: 14,
                            boxShadow: "2px 0 4px -2px rgba(0,0,0,0.08)",
                          }
                        : {};
                      return (
                        <TableCell
                          key={col.key}
                          className={
                            isRestricted
                              ? classes.restrictedHeader
                              : classes.tableHeader
                          }
                          sx={{
                            zIndex: 10,
                            minWidth:
                              APP_CONSTANTS.SMALL_WIDTH_COLUMNS.includes(
                                col.key
                              )
                                ? 50
                                : col.key === "description"
                                ? 550
                                : col.user_edit_access
                                ? 300
                                : 200,
                            ...stickyHead,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                              whiteSpace: "nowrap",
                              color: isRestricted
                                ? theme.palette.brain.table.header
                                    .restrictedText
                                : "inherit",
                            }}
                          >
                            <span>{col.label}</span>
                            {col.is_editable_column === false ? (
                              <Tooltip title={col.info} arrow placement="top">
                                <InfoOutlinedIcon
                                  sx={{
                                    fontSize: 13,
                                    color: theme.palette.brain.grey.text,
                                    cursor: "pointer",
                                  }}
                                />
                              </Tooltip>
                            ) : col.user_edit_access === true ? (
                              <>
                                <Tooltip title={col.info} arrow placement="top">
                                  <InfoOutlinedIcon
                                    sx={{
                                      fontSize: 13,
                                      color: theme.palette.brain.grey.text,
                                      cursor: "pointer",
                                    }}
                                  />
                                </Tooltip>
                                <Tooltip
                                  arrow
                                  placement="top"
                                  title={
                                    col.editors?.length ? (
                                      <Box sx={{ p: 0.5 }}>
                                        <Typography
                                          sx={{
                                            fontWeight: 600,
                                            mb: 0.5,
                                            fontSize: "12px",
                                          }}
                                        >
                                          Edit access:
                                        </Typography>
                                        {col.editors.map((owner, index) => (
                                          <Typography
                                            key={index}
                                            sx={{
                                              lineHeight: 1.5,
                                              fontSize: "12px",
                                            }}
                                          >
                                            • {owner.email}
                                          </Typography>
                                        ))}
                                      </Box>
                                    ) : (
                                      "No reviewer assigned"
                                    )
                                  }
                                >
                                  <GroupOutlinedIcon
                                    sx={{
                                      fontSize: 16,
                                      color: theme.palette.brain.grey.text,
                                      cursor: "pointer",
                                    }}
                                  />
                                </Tooltip>
                                <Box onClick={() => handleSortColumn(col)}>
                                  {sortkey?.sortColumn !== col.key ? (
                                    <Tooltip
                                      title="Sort by confidence score"
                                      arrow
                                      placement="top"
                                    >
                                      <BarChartIcon
                                        sx={{
                                          fontSize: 14,
                                          color: theme.palette.brain.grey.text,
                                          cursor: "pointer",
                                          verticalAlign: "middle",
                                        }}
                                      />
                                    </Tooltip>
                                  ) : sortkey?.direction === "asc" ? (
                                    <ArrowUpwardIcon
                                      sx={{
                                        fontSize: 14,
                                        color: theme.palette.brain.grey.text,
                                        cursor: "pointer",
                                        verticalAlign: "middle",
                                      }}
                                    />
                                  ) : (
                                    <ArrowDownwardIcon
                                      sx={{
                                        fontSize: 14,
                                        color: theme.palette.brain.grey.text,
                                        cursor: "pointer",
                                        verticalAlign: "middle",
                                      }}
                                    />
                                  )}
                                </Box>
                              </>
                            ) : (
                              <>
                                <Tooltip title={col.info} arrow placement="top">
                                  <InfoOutlinedIcon
                                    sx={{
                                      fontSize: 13,
                                      color: theme.palette.brain.grey.text,
                                      cursor: "pointer",
                                    }}
                                  />
                                </Tooltip>
                                <LockOutlinedIcon
                                  sx={{
                                    fontSize: 14,
                                    color: theme.palette.brain.grey.text,
                                  }}
                                />
                              </>
                            )}

                            {APP_CONSTANTS.FREEZABLE_COLUMNS.includes(
                              col.key
                            ) && (
                              <Box
                                sx={{
                                  flex: 1,
                                  display: "flex",
                                  justifyContent: "end",
                                }}
                              >
                                <Tooltip
                                  title={
                                    isFrozen
                                      ? "Unfreeze column"
                                      : "Freeze column"
                                  }
                                  arrow
                                >
                                  <IconButton
                                    size="small"
                                    onClick={() => toggleFreeze(col.key)}
                                    sx={{ p: 0.25 }}
                                  >
                                    {isFrozen ? (
                                      <PushPinIcon
                                        sx={{
                                          fontSize: 14,
                                          color:
                                            theme.palette.brain.table.header
                                              .restrictedText,
                                        }}
                                      />
                                    ) : (
                                      <PushPinOutlinedIcon
                                        sx={{
                                          fontSize: 14,
                                          color: theme.palette.brain.grey.text,
                                        }}
                                      />
                                    )}
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            )}
                          </Box>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                )}
                itemContent={(idx, row) => (
                  <MemoizedRowCells
                    row={row}
                    idx={idx}
                    columns={activeColumns}
                    isSelected={selectedIds.has(row.id)}
                    onToggleSelect={toggleSelect}
                    onCellChange={handleCellChange}
                    onTagCellChange={handleCellChange}
                    has_editable_columns={has_editable_columns}
                    editedCols={rowEdits[row.id] || EMPTY_ARRAY}
                    theme={theme}
                    classes={classes}
                    frozenColumns={frozenColumns}
                    rowBg={() => {
                      return idx % 2 === 0
                        ? theme.palette.brain.drawer.item_background
                        : theme.palette.brain.table.row.background;
                    }}
                    frozenLeftOffsets={frozenLeftOffsets}
                    skuCount={skuRecords?.[row.id]?.length || 0}
                    skuList={(skuRecords?.[row.id] || []).map(
                      (item, _) => item.sku
                    )}
                    handleSkuClick={openSkuForUpc}
                    fuseByColumn={fuseByColumn}
                  />
                )}
              />
            )}
          </Paper>
        ) : (
          <CompetitorsData
            theme={theme}
            loadingRows={loadingRows}
            loadingColumns={loadingColumns}
            rows={rows}
            virtuosoComponents={virtuosoComponents}
            classes={classes}
            displayedColumns={activeColumns}
            frozenColumns={frozenColumns}
            columnData={columnData}
            toggleFreeze={toggleFreeze}
            skuRecords={skuRecords}
            openSkuForUpc={openSkuForUpc}
          />
        )}

        {/* Pagination */}
        {pagination && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              px: 2,
              py: 1,
              borderRadius: 1.5,
              backgroundColor: theme.palette.brain.cyanBlue.background,
              border: `1px solid ${theme.palette.brain.cyanBlue.border}`,
            }}
          >
            <Typography
              variant="body2"
              sx={{ fontSize: "0.72rem", color: theme.palette.primary.text2 }}
            >
              {pagination
                ? `Showing ${(page - 1) * selectedPageSize + 1} – ${Math.min(
                    page * selectedPageSize,
                    pagination.total_records
                  )} of ${pagination.total_records}`
                : ""}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography
                variant="body2"
                sx={{
                  fontSize: "0.75rem",
                  color: theme.palette.primary.text2,
                  fontWeight: 500,
                }}
              >
                Page Size:
              </Typography>
              <DisplayPageSize
                theme={theme}
                selectedPageSize={selectedPageSize}
                handlePageSizeChange={handlePageSizeChange}
              />
              <IconButton
                size="small"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1 || loadingRows}
                sx={{ color: theme.palette.secondary.main2 }}
              >
                <NavigateBeforeIcon />
              </IconButton>
              <Typography
                variant="body2"
                sx={{
                  fontSize: "0.72rem",
                  color: theme.palette.primary.text2,
                }}
              >
                Page {page} of {pagination?.total_pages}
              </Typography>
              <IconButton
                size="small"
                onClick={() =>
                  setPage((p) => Math.min(pagination?.total_pages, p + 1))
                }
                disabled={page === pagination?.total_pages || loadingRows}
                sx={{ color: theme.palette.secondary.main2 }}
              >
                <NavigateNextIcon />
              </IconButton>
            </Box>
          </Box>
        )}
      </>

      <ConfirmDialog
        confirmOpen={confirmOpen}
        setConfirmOpen={setConfirmOpen}
        totalSubmitRows={selectedIds?.size || 0}
        editCount={editCount}
        theme={theme}
        handleSubmit={handleSubmit}
      />

      <ConflictDialog
        open={conflictOpen}
        onClose={() => setConflictOpen(false)}
        conflicts={conflicts}
        columns={activeColumns}
      />

      {/* SKU Drawer */}
      {/* <SKUDetailsDrawer open={selectedSkuData.open} /> */}
      {skuDrawer?.open && (
        <SKUDetailsDrawer
          open={skuDrawer?.open}
          data={skuDrawer.data}
          onClose={() => {
            setSkuDrawer({ open: false, data: {} });
          }}
        />
      )}
    </Box>
  );
}
